import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import '../../assets/styles/auth.css';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('no-token');
        return;
      }

      try {
        await authService.verifyEmail(token);
        setStatus('success');
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.error || 'Verification failed. The link may have expired.');
      }
    };

    verifyEmail();
  }, [token, navigate]);

  const handleResend = async (e) => {
    e.preventDefault();
    setResendLoading(true);
    setResendSuccess(false);

    try {
      await authService.resendVerification(resendEmail);
      setResendSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend verification email.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-form-section">
          <Link to="/" className="auth-logo">
            <i className="fas fa-shield-alt"></i>
            <span>SafeWork<span className="brand-360">360</span></span>
          </Link>

          {status === 'verifying' && (
            <div className="text-center">
              <div className="spinner-border text-primary mb-4" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Verifying...</span>
              </div>
              <div className="auth-header">
                <h1>Verifying your email...</h1>
                <p>Please wait while we verify your email address.</p>
              </div>
            </div>
          )}

          {status === 'success' && (
            <>
              <div className="text-center mb-4">
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  borderRadius: '50%', 
                  background: '#dcfce7', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem'
                }}>
                  <i className="fas fa-check" style={{ fontSize: '2.5rem', color: '#16a34a' }}></i>
                </div>
              </div>
              
              <div className="auth-header">
                <h1>Email verified!</h1>
                <p>Your email has been successfully verified. You'll be redirected to the login page shortly.</p>
              </div>

              <div className="auth-form">
                <Link to="/login" className="btn-auth-primary">
                  <i className="fas fa-sign-in-alt"></i>
                  Continue to Sign In
                </Link>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="text-center mb-4">
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  borderRadius: '50%', 
                  background: '#fee2e2', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem'
                }}>
                  <i className="fas fa-times" style={{ fontSize: '2.5rem', color: '#dc2626' }}></i>
                </div>
              </div>

              <div className="auth-header">
                <h1>Verification failed</h1>
                <p>{error}</p>
              </div>

              {resendSuccess ? (
                <div className="auth-alert success">
                  <i className="fas fa-check-circle"></i>
                  <span>Verification email sent! Check your inbox.</span>
                </div>
              ) : (
                <form className="auth-form" onSubmit={handleResend}>
                  <div className="form-group">
                    <label htmlFor="email">Request a new verification email</label>
                    <div className="input-icon-wrapper">
                      <i className="fas fa-envelope input-icon"></i>
                      <input
                        type="email"
                        id="email"
                        className="form-control"
                        placeholder="Enter your email"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <button type="submit" className="btn-auth-primary" disabled={resendLoading}>
                    {resendLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane"></i>
                        Resend verification email
                      </>
                    )}
                  </button>
                </form>
              )}
            </>
          )}

          {status === 'no-token' && (
            <>
              <div className="auth-header">
                <h1>Invalid verification link</h1>
                <p>The verification link appears to be invalid or has expired.</p>
              </div>

              {resendSuccess ? (
                <div className="auth-alert success">
                  <i className="fas fa-check-circle"></i>
                  <span>Verification email sent! Check your inbox.</span>
                </div>
              ) : (
                <form className="auth-form" onSubmit={handleResend}>
                  <div className="form-group">
                    <label htmlFor="email">Request a new verification email</label>
                    <div className="input-icon-wrapper">
                      <i className="fas fa-envelope input-icon"></i>
                      <input
                        type="email"
                        id="email"
                        className="form-control"
                        placeholder="Enter your email"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <button type="submit" className="btn-auth-primary" disabled={resendLoading}>
                    {resendLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane"></i>
                        Send verification email
                      </>
                    )}
                  </button>
                </form>
              )}
            </>
          )}

          <div className="auth-footer">
            <Link to="/login">Back to Sign In</Link>
          </div>
        </div>

        {/* Features Section */}
        <div className="auth-features-section">
          <div className="features-content">
            <h2>Almost there!</h2>
            <p>Verifying your email helps us keep your account secure and ensures you receive important notifications.</p>
            
            <div className="feature-list">
              <div className="feature-item">
                <div className="feature-item-icon">
                  <i className="fas fa-bell"></i>
                </div>
                <div className="feature-item-text">
                  <h4>Stay Informed</h4>
                  <p>Receive alerts for compliance deadlines and issues</p>
                </div>
              </div>
              
              <div className="feature-item">
                <div className="feature-item-icon">
                  <i className="fas fa-user-shield"></i>
                </div>
                <div className="feature-item-text">
                  <h4>Account Security</h4>
                  <p>Verified emails enable password recovery</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
